'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import partial from 'react-utils/partial';
import Branding from 'MeetingsBase/models/Branding';
import { getPrimaryButtonStyle, getPrimaryButtonHoverStyle, getSecondaryButtonStyle, getSecondaryButtonHoverStyle } from 'MeetingsLib/utils/color';
import PreviewContext from 'MeetingsLib/utils/PreviewContext';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { BATTLESHIP, GYPSUM } from 'HubStyleTokens/colors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BUTTON_WIDTH = '102px';
const FooterContainer = styled(UIBox).withConfig({
  displayName: "StepButtonsFooter__FooterContainer",
  componentId: "sc-15uyecb-0"
})(["flex-grow:0;padding-left:32px;padding-right:32px;", ""], ({
  compact
}) => compact ? `
    background: ${GYPSUM};
    border-top: solid 1px ${BATTLESHIP};
    padding-top: 16px;
    padding-bottom: 18px;
  ` : `padding-top: 28px;
         padding-bottom: 40px;`);
export default class StepButtonsFooter extends Component {
  constructor(props) {
    super(props);
    this.onMouseEnter = newState => {
      this.setState(newState);
    };
    this.onMouseLeave = newState => {
      this.setState(newState);
    };
    this.getForwardButtonKey = () => {
      const lastStepKey = this.props.offline ? 'buttons.confirm.offline' : 'buttons.confirm.online';
      return this.props.isLastStep ? lastStepKey : 'buttons.next';
    };
    this.getForwardButtonVisibility = () => {
      return this.props.goToNextStep ? 'visible' : 'hidden';
    };
    this.getBackFunction = isPreview => {
      const {
        compact,
        goToPreviousStep,
        isLastStep
      } = this.props;
      if (isPreview && (!compact || isLastStep !== undefined)) return;
      goToPreviousStep();
    };
    this.getProceedFunction = () => {
      const {
        goToNextStep,
        goToPreviousStep
      } = this.props;
      return goToNextStep ? partial(goToNextStep, goToPreviousStep) : null;
    };
    this.renderBackButton = () => {
      const {
        goToPreviousStep,
        branding: {
          primaryColor
        },
        compact,
        disableBackButton,
        backButtonTooltipTitle
      } = this.props;
      if (!goToPreviousStep) {
        return null;
      }
      const brandingStyle = this.state.backButtonHover ? getSecondaryButtonHoverStyle(primaryColor) : getSecondaryButtonStyle(primaryColor);
      const backButton = /*#__PURE__*/_jsx(PreviewContext.Consumer, {
        children: isPreview => /*#__PURE__*/_jsxs(UIButton, {
          style: Object.assign({
            minWidth: BUTTON_WIDTH,
            marginLeft: 0
          }, brandingStyle),
          onClick: () => this.getBackFunction(isPreview),
          disabled: disableBackButton,
          use: "secondary",
          size: compact ? 'small' : 'default',
          responsive: false,
          "data-test-id": "back-btn",
          "data-selenium-test": "back-button",
          onMouseEnter: partial(this.onMouseEnter, {
            backButtonHover: true
          }),
          onMouseLeave: partial(this.onMouseLeave, {
            backButtonHover: false
          }),
          children: [/*#__PURE__*/_jsx(UIIcon, {
            className: "back-button-icon",
            name: "left"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "buttons.back"
          })]
        })
      });
      if (backButtonTooltipTitle) {
        return /*#__PURE__*/_jsx(UITooltip, {
          title: backButtonTooltipTitle,
          disabled: !disableBackButton,
          children: backButton
        });
      }
      return backButton;
    };
    this.renderForwardButton = () => {
      const {
        submittingMeeting,
        canProceed,
        tooltipTitle,
        branding: {
          primaryColor
        },
        compact
      } = this.props;
      const visibility = this.getForwardButtonVisibility();
      const messageKey = this.getForwardButtonKey();
      const goToNextStep = this.getProceedFunction();
      const brandingStyle = this.state.forwardButtonHover ? getPrimaryButtonHoverStyle(primaryColor) : getPrimaryButtonStyle(primaryColor);
      const forwardButton = /*#__PURE__*/_jsx(PreviewContext.Consumer, {
        children: isPreview => /*#__PURE__*/_jsx(UILoadingButton, {
          style: Object.assign({
            minWidth: BUTTON_WIDTH,
            visibility
          }, brandingStyle),
          "data-test-id": "forward-btn",
          loading: submittingMeeting || false,
          onClick: isPreview ? null : goToNextStep,
          use: "primary",
          size: compact ? 'small' : 'medium',
          responsive: false,
          disabled: !canProceed,
          "data-selenium-test": "forward-button",
          onMouseEnter: partial(this.onMouseEnter, {
            forwardButtonHover: true
          }),
          onMouseLeave: partial(this.onMouseLeave, {
            forwardButtonHover: false
          }),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: messageKey
          })
        })
      });
      if (tooltipTitle) {
        return /*#__PURE__*/_jsx(UITooltip, {
          title: tooltipTitle,
          disabled: canProceed,
          children: /*#__PURE__*/_jsx("span", {
            children: forwardButton
          })
        });
      }
      return forwardButton;
    };
    this.state = {
      forwardButtonHover: false,
      backButtonHover: false
    };
  }
  render() {
    const {
      compact
    } = this.props;
    return /*#__PURE__*/_jsx(FooterContainer, {
      className: "width-100",
      compact: compact,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        direction: "row-reverse",
        align: "end",
        justify: "between",
        children: [this.renderForwardButton(), this.renderBackButton()]
      })
    });
  }
}
StepButtonsFooter.propTypes = {
  submittingMeeting: PropTypes.bool,
  goToPreviousStep: PropTypes.func,
  goToNextStep: PropTypes.func,
  isLastStep: PropTypes.bool,
  canProceed: PropTypes.bool,
  disableBackButton: PropTypes.bool,
  backButtonTooltipTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  offline: PropTypes.bool,
  tooltipTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  compact: PropTypes.bool,
  branding: PropTypes.instanceOf(Branding).isRequired
};